import * as angular from "angular";
import { escape } from "lodash";

import choicesSelect from "../../components/choices-select/choices-select";
import controlTemplate from "./schema-form-choices-select-control.html";
import fieldTemplate from "./schema-form-choices-select-field.html";

export interface SchemaFormTitleMapItem {
	name?: unknown;
	value: unknown;
}

export interface SchemaFormSelectDefinition {
	title?: string;
	titleMap?: readonly SchemaFormTitleMapItem[];
	readonly?: boolean;
}

export interface SchemaFormChoice {
	value: string;
	label: string;
	modelValue: unknown;
	customProperties: {
		searchBy: string[];
	};
}

export interface SchemaFormChoicesBox {
	clearSelected(): void;
	setChoices(
		choices: readonly SchemaFormChoice[],
		valueProperty: string,
		labelProperty: string,
		replaceChoices: boolean
	): unknown;
	setValueByChoice(value: string): unknown;
}

export function buildSchemaFormChoices(
	titleMap: readonly SchemaFormTitleMapItem[] = []
): SchemaFormChoice[] {
	return titleMap.map((item, index) => {
		const name = String(item.name ?? item.value ?? "");
		const modelValue = String(item.value ?? "");

		return {
			value: String(index),
			label: escape(name),
			modelValue: item.value,
			customProperties: {
				searchBy: [...new Set([name, modelValue])]
			}
		};
	});
}

export class SchemaFormChoicesSelectController {
	public static $inject = ["$scope"];

	public field!: SchemaFormSelectDefinition;
	public selectId: string;
	public choices: readonly SchemaFormChoice[] = [];
	public selectedChoiceValue?: string;

	private ngModelCtrl!: angular.INgModelController;
	private choicesBox?: SchemaFormChoicesBox;

	public constructor(private readonly $scope: angular.IScope) {}

	public initialize(
		field: SchemaFormSelectDefinition,
		selectId: string,
		ngModelCtrl: angular.INgModelController
	): void {
		this.field = field;
		this.selectId = selectId;
		this.ngModelCtrl = ngModelCtrl;
		this.ngModelCtrl.$render = () => this.renderModelValue();
		this.$scope.$watchCollection(
			() => this.field.titleMap,
			titleMap => this.setTitleMap(titleMap)
		);
	}

	public setChoicesBox(box: SchemaFormChoicesBox): void {
		this.choicesBox = box;
		this.populateChoicesBox();
	}

	private setTitleMap(titleMap?: readonly SchemaFormTitleMapItem[]): void {
		this.choices = buildSchemaFormChoices(titleMap);

		if(this.choicesBox) {
			this.populateChoicesBox();
		}
	}

	private populateChoicesBox(): void {
		const box = this.choicesBox;
		if(!box) {
			return;
		}

		box.clearSelected();
		box.setChoices(this.choices, "value", "label", true);
		this.renderModelValue();
	}

	public getSelectedDisplay(value: string | undefined): string {
		return this.choices.find(choice => choice.value === value)?.label ?? "";
	}

	public handleValueChanged(value: string | undefined): void {
		const modelValue = this.choices.find(choice => choice.value === value)?.modelValue;

		if(!angular.equals(this.ngModelCtrl.$viewValue, modelValue)) {
			this.ngModelCtrl.$setViewValue(modelValue);
		}
	}

	private renderModelValue(): void {
		const selectedChoice = this.choices
			.find(choice => angular.equals(choice.modelValue, this.ngModelCtrl.$viewValue));
		this.selectedChoiceValue = selectedChoice?.value;

		if(!this.choicesBox) {
			return;
		}

		this.choicesBox.clearSelected();
		if(selectedChoice) {
			this.choicesBox.setValueByChoice(selectedChoice.value);
		}
	}
}

export function schemaFormChoicesSelectAddOn(
	schemaFormDecoratorsProvider,
	sfBuilderProvider
): void {
	schemaFormDecoratorsProvider.defineAddOn(
		"bootstrapDecorator",
		"select",
		"./schema-form-choices-select-field.html",
		sfBuilderProvider.stdBuilders
	);
}

interface SchemaFormFieldScope extends angular.IScope {
	form: SchemaFormSelectDefinition;
}

export function schemaFormChoicesSelectDirective(): angular.IDirective {
	return {
		restrict: "E",
		template: controlTemplate,
		controller: SchemaFormChoicesSelectController,
		controllerAs: "$choicesSelect",
		require: ["schemaFormChoicesSelect", "ngModel"],
		compile: () => ({
			pre: (
				scope: SchemaFormFieldScope,
				_element: angular.IAugmentedJQuery,
				_attrs: angular.IAttributes,
				controllers: [SchemaFormChoicesSelectController, angular.INgModelController]
			) => {
				const [controller, ngModelCtrl] = controllers;
				controller.initialize(
					scope.form,
					`schema-form:select:${scope.$id}`,
					ngModelCtrl
				);
			}
		})
	};
}

export default angular
	.module("vesl.extensions.json-schema.schema-form-choices-select", [
		"schemaForm",
		choicesSelect
	])
	.run(["$templateCache", function($templateCache) {
		$templateCache.put("./schema-form-choices-select-field.html", fieldTemplate);
	}])
	.config(schemaFormChoicesSelectAddOn)
	.directive("schemaFormChoicesSelect", schemaFormChoicesSelectDirective)
	.name;
